import { cn } from "cn";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "#/components/Button/Button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/Tooltip/Tooltip";
import { CopyButton } from "../CopyButton/CopyButton";

type CodeExampleProps = {
	code: string;
	/** Defaulting to true to be on the safe side; you should have to opt out of the secure option, not remember to opt in */
	secret?: boolean;
	/** Redact parts of the code if the user doesn't want to obfuscate the whole code */
	redactPattern?: RegExp;
	/** Replacement text for redacted content */
	redactReplacement?: string;
	/** Show a button to reveal the redacted parts of the code */
	showRevealButton?: boolean;
	className?: string;
};

/**
 * Component to show single-line code examples, with a copy button
 */
export const CodeExample: React.FC<CodeExampleProps> = ({
	code,
	className,
	secret = true,
	redactPattern,
	redactReplacement = "********",
	showRevealButton,
}) => {
	const [showFullValue, setShowFullValue] = useState(false);

	const displayValue = secret
		? obfuscateText(code)
		: redactPattern && !showFullValue
			? code.replace(redactPattern, redactReplacement)
			: code;

	const showButtonLabel = showFullValue
		? "Hide sensitive data"
		: "Show sensitive data";
	const icon = showFullValue ? (
		<EyeOffIcon className="size-4" />
	) : (
		<EyeIcon className="size-4" />
	);

	return (
		<div
			className={cn(
				"cursor-pointer flex flex-row items-center",
				"text-content-primary font-mono text-sm",
				"rounded-lg p-2 leading-normal border border-solid",
				"hover:bg-surface-secondary",
				className,
			)}
		>
			<code
				className={cn([
					"px-2 py-0 grow break-all",
					secret && "[-webkit-text-security:disc]", // also supported by firefox
				])}
			>
				{secret ? (
					<>
						{/*
						 * Obfuscating text even though we have the characters replaced with
						 * discs in the CSS for two reasons:
						 * 1. The CSS property is non-standard and won't work everywhere;
						 *    MDN warns you not to rely on it alone in production
						 * 2. Even with it turned on and supported, the plaintext is still
						 *    readily available in the HTML itself
						 */}
						<span aria-hidden>{displayValue}</span>
						<span className="sr-only">
							Encrypted text. Please access via the copy button.
						</span>
					</>
				) : (
					displayValue
				)}
			</code>

			<div className="flex items-center gap-1 select-none">
				{showRevealButton && redactPattern && !secret && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								size="icon"
								variant="subtle"
								aria-label={showButtonLabel}
								onClick={() => setShowFullValue(!showFullValue)}
							>
								{icon}
							</Button>
						</TooltipTrigger>
						<TooltipContent>{showButtonLabel}</TooltipContent>
					</Tooltip>
				)}
				<CopyButton text={code} label="Copy code" />
			</div>
		</div>
	);
};

function obfuscateText(text: string): string {
	return new Array(text.length).fill("*").join("");
}
